import React, { useEffect, useState, useMemo } from "react";
import { getEarnings, downloadPayslip } from "../../ApiHelper/url_Helpers";
import CommonTable from "../../Components/CommonTable";
import Loader from "../../Components/Loader";
import dayjs from "dayjs";
import { Eye } from "lucide-react";
import { sortPayslips } from "../Dashboard/DashboardData";
import SortDropdown from "../../Components/SortDropDown";
import EarningFilter from "./EarningFilter";
import { DocumentDownload } from "iconsax-react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

export const Earnings = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [earnings, setEarnings] = useState({
    count: 0,
    next: null,
    previous: null,
    results: [],
  });

  const [loading, setLoading] = useState(false);

  // Derive state from URL
  const filters = useMemo(() => {
    return {
      filter_year: searchParams.get("filter_year") || "",
      filter_month: searchParams.get("filter_month") || "",
      sort_by: searchParams.get("sort_by") || "",
    };
  }, [searchParams]);

  const page = useMemo(() => Number(searchParams.get("page")) || 1, [searchParams]);

  // Fetch API
  const fetchEarnings = async (query = "") => {
    try {
      setLoading(true);
      const data = await getEarnings(query ? `?${query}` : "");
      setEarnings(data);
    } catch (e) {
      console.error("Error fetching earnings", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEarnings(searchParams.toString());
  }, [location.search]); // Re-fetch on URL change

  const applyFilters = (newFilters) => {
    const nextParams = new URLSearchParams(searchParams);
    
    // Reset to page 1 when filters change (except when only page changes)
    if (!newFilters.page) {
      nextParams.delete("page");
    }

    Object.entries(newFilters).forEach(([key, value]) => {
      if (value) {
        nextParams.set(key, value);
      } else {
        nextParams.delete(key);
      }
    });

    setSearchParams(nextParams, { replace: true });
  };

  // Pagination
  const handlePageChange = (apiUrl) => {
    if (!apiUrl) return;
    const base = process.env.REACT_APP_API_BASE_URL || window.location.origin;
    const apiParams = new URL(apiUrl, base).searchParams;
    const pageVal = apiParams.get("page") || "1";
    
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("page", pageVal);
    setSearchParams(nextParams, { replace: true });
  };

  const handleReset = () => {
    setSearchParams({}, { replace: true });
  };

  // Download Payslip
  const handleDownload = async (application_id) => {
    try {
      const res = await downloadPayslip(application_id);
      const url = window.URL.createObjectURL(res);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Payslip_${application_id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Download failed", err);
    }
  };

  return (
    <div className="p-3 md:p-8 bg-gray-50 min-h-screen">
      <header className="mb-6">
        <h2 className="text-xl font-semibold">Payslips</h2>
        <p className="text-sm text-gray-500">
          View your salary details and download payslips
        </p>
      </header>

      <div className="flex flex-wrap justify-end gap-3 mb-6">
        <EarningFilter
          value={filters}
          onChange={(patch) => {
            const updatedPatch = {};
            if (patch.month !== undefined) updatedPatch.filter_month = patch.month;
            if (patch.year !== undefined) updatedPatch.filter_year = patch.year;
            applyFilters(updatedPatch);
          }}
        />

        <SortDropdown
          value={filters.sort_by}
          options={sortPayslips}
          onChange={(ordering) => applyFilters({ sort_by: ordering })}
        />

        <button
          onClick={handleReset}
          className="border rounded-md px-4 py-2 text-sm text-gray-600 hover:bg-gray-100"
        >
          Reset All
        </button>
      </div>

      {loading ? (
        <Loader />
      ) : (
        <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
          <CommonTable
            data={earnings?.results || []}
            rowKey="payslip_id"
            columns={[
              { key: "month", label: "Month" },
              { key: "year", label: "Year" },
              { key: "gross_pay", label: "Gross Salary" },
              { key: "net_pay", label: "Net Salary" },
              {
                key: "credited_on",
                label: "Credited On",
                render: (v) => dayjs(v).format("DD-MM-YYYY"),
              },
            ]}
            rowActions={(row) => (
              <div className="flex justify-start items-center">
                {/* <a 
                  // href={row.file_url}
                  // target="_blank"
                  // rel="noreferrer"
                  // className="p-1.5 hover:bg-blue-50 rounded"
                // >
                   <Eye 
                    // size={20}
                    // className="text-gray-500 cursor-pointer hover:text-[#00B8D9]"
                    // title="View Document"
                  // />
                 </a> */}
                <button
                  onClick={() => handleDownload(row?.id)}
                  title="Download Payslip"
                  className="text-gray-400 hover:text-blue-600 transition duration-150 p-2 rounded-lg hover:bg-blue-50"
                >
                  <DocumentDownload size={20} color="#6B7280" />
                </button>
              </div>
            )}
            nextPageUrl={earnings.next}
            prevPageUrl={earnings.previous}
            onPageChange={handlePageChange}
            page={page}
            totalCount={earnings?.count}
          />
        </div>
      )}
    </div>
  );
};
